import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BrainCircuit,
  ChartNoAxesCombined,
  HeartHandshake,
  Route,
  Sparkles,
  Users,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import PublicSiteHeader from '../components/public/PublicSiteHeader';
import StravaPoweredByBadge from '../components/brand/StravaPoweredByBadge';
import { useLanguage } from '../contexts/LanguageContext';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const HomePage = () => {
  const { t } = useLanguage();

  const highlights = [
    {
      icon: Route,
      title: t('marketing.hero.cards.trackTitle'),
      description: t('marketing.hero.cards.trackDescription'),
    },
    {
      icon: HeartHandshake,
      title: t('marketing.hero.cards.coachTitle'),
      description: t('marketing.hero.cards.coachDescription'),
    },
    {
      icon: BrainCircuit,
      title: t('marketing.hero.cards.aiTitle'),
      description: t('marketing.hero.cards.aiDescription'),
    },
  ];

  const featureCards = [
    {
      icon: ChartNoAxesCombined,
      title: t('marketing.features.cards.trainingTitle'),
      description: t('marketing.features.cards.trainingDescription'),
    },
    {
      icon: HeartHandshake,
      title: t('marketing.features.cards.coachingTitle'),
      description: t('marketing.features.cards.coachingDescription'),
    },
    {
      icon: Sparkles,
      title: t('marketing.features.cards.intelligenceTitle'),
      description: t('marketing.features.cards.intelligenceDescription'),
    },
    {
      icon: Users,
      title: t('marketing.features.cards.communityTitle'),
      description: t('marketing.features.cards.communityDescription'),
    },
  ];

  const methodCards = [
    {
      step: '01',
      title: t('marketing.method.planTitle'),
      description: t('marketing.method.planDescription'),
    },
    {
      step: '02',
      title: t('marketing.method.adaptTitle'),
      description: t('marketing.method.adaptDescription'),
    },
    {
      step: '03',
      title: t('marketing.method.progressTitle'),
      description: t('marketing.method.progressDescription'),
    },
  ];

  return (
    <div className='min-h-screen bg-background text-foreground'>
      <section className='relative overflow-hidden bg-[hsl(var(--brand-navy))] text-white'>
        <div
          className='absolute inset-0 bg-cover bg-center'
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(5, 11, 24, 0.88) 0%, rgba(5, 11, 24, 0.52) 45%, rgba(5, 11, 24, 0.68) 100%), url('/brand/illustrations/zitius-community-hero.png')",
          }}
        />
        <div className='absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(30,151,244,0.32),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(7,28,67,0.8),transparent_42%)]' />
        <div className='absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-black/30 to-transparent' />

        <div className='relative z-10'>
          <PublicSiteHeader onDarkHero />

          <div className='mx-auto flex min-h-[calc(100vh-5.5rem)] w-full max-w-7xl items-end px-4 pb-16 pt-12 sm:px-6 lg:items-center lg:pb-20'>
            <div className='w-full'>
              <motion.div
                initial='hidden'
                animate='visible'
                variants={fadeUp}
                transition={{ duration: 0.6 }}
                className='max-w-3xl'
              >
                <Badge className='mb-5 rounded-full border border-white/20 bg-white/10 px-4 py-1 text-white hover:bg-white/10'>
                  {t('marketing.hero.eyebrow')}
                </Badge>

                <h1 className='max-w-3xl text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl'>
                  {t('marketing.hero.title')}
                </h1>
                <p className='mt-5 max-w-2xl text-base text-white/78 sm:text-lg lg:text-xl'>
                  {t('marketing.hero.subtitle')}
                </p>
              </motion.div>

              <div className='mt-8 grid grid-cols-1 items-center gap-4 md:grid-cols-3'>
                <div className='flex flex-col gap-3 sm:flex-row md:col-span-2'>
                  <Button
                    asChild
                    size='lg'
                    className='rounded-full bg-[hsl(var(--brand-electric))] px-7 text-white shadow-[0_20px_40px_rgba(30,151,244,0.28)] hover:bg-[hsl(var(--brand-electric-strong))]'
                  >
                    <Link to='/login2'>
                      {t('marketing.cta.primary')}
                      <ArrowRight className='ml-2 h-4 w-4' />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size='lg'
                    variant='outline'
                    className='rounded-full border-white/20 bg-white/10 px-7 text-white hover:bg-white/20 hover:text-white'
                  >
                    <Link to='/features'>{t('marketing.cta.secondary')}</Link>
                  </Button>
                </div>
                <div className='flex justify-end md:justify-end'>
                  <StravaPoweredByBadge />
                </div>
              </div>

              <motion.div
                className='mt-10 grid gap-4 md:grid-cols-3'
                initial='hidden'
                animate='visible'
                transition={{ staggerChildren: 0.08, delayChildren: 0.2 }}
              >
                {highlights.map(({ icon: Icon, title, description }) => (
                  <motion.div key={title} variants={fadeUp} transition={{ duration: 0.55 }}>
                    <Card className='h-full border-white/12 bg-white/10 text-white shadow-[0_12px_28px_rgba(3,9,20,0.22)] backdrop-blur-md'>
                      <CardContent className='space-y-3 p-5'>
                        <div className='flex h-11 w-11 items-center justify-center rounded-full bg-white/10'>
                          <Icon className='h-5 w-5 text-[hsl(var(--brand-electric-soft))]' />
                        </div>
                        <div>
                          <h2 className='text-base font-semibold'>{title}</h2>
                          <p className='mt-2 text-sm leading-6 text-white/72'>{description}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <main className='relative z-10'>
        <section id='features' className='mx-auto max-w-7xl px-4 py-20 sm:px-6'>
          <SectionHeading
            eyebrow={t('marketing.features.eyebrow')}
            title={t('marketing.features.title')}
            description={t('marketing.features.description')}
          />

          <div className='mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4'>
            {featureCards.map(({ icon: Icon, title, description }) => (
              <Card key={title} className='border-border/60 bg-card/90 shadow-sm'>
                <CardContent className='space-y-4 p-6'>
                  <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(var(--brand-electric-soft))]/15 text-[hsl(var(--brand-navy))]'>
                    <Icon className='h-5 w-5' />
                  </div>
                  <div>
                    <h3 className='text-lg font-semibold text-card-foreground'>{title}</h3>
                    <p className='mt-2 text-sm leading-6 text-muted-foreground'>{description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section id='method' className='bg-[hsl(var(--brand-surface))]'>
          <div className='mx-auto max-w-7xl px-4 py-20 sm:px-6'>
            <SectionHeading
              eyebrow={t('marketing.method.eyebrow')}
              title={t('marketing.method.title')}
              description={t('marketing.method.description')}
            />

            <div className='mt-10 grid gap-6 lg:grid-cols-3'>
              {methodCards.map(({ step, title, description }) => (
                <Card key={step} className='border-border/70 bg-card shadow-sm'>
                  <CardContent className='p-6'>
                    <p className='text-sm font-medium uppercase tracking-[0.24em] text-[hsl(var(--brand-electric))]'>
                      {step}
                    </p>
                    <h3 className='mt-5 text-2xl font-semibold text-card-foreground'>{title}</h3>
                    <p className='mt-3 text-sm leading-7 text-muted-foreground'>{description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id='community' className='mx-auto max-w-7xl px-4 py-20 sm:px-6'>
          <div className='grid gap-8 lg:grid-cols-[1.2fr_0.8fr]'>
            <Card className='overflow-hidden border-border/60 bg-[linear-gradient(135deg,rgba(10,36,88,0.96),rgba(10,36,88,0.88))] text-white shadow-[0_24px_60px_rgba(10,36,88,0.22)]'>
              <CardContent className='p-8 sm:p-10'>
                <Badge className='rounded-full bg-white/10 text-white hover:bg-white/10'>
                  {t('marketing.community.eyebrow')}
                </Badge>
                <h2 className='mt-5 text-3xl font-semibold leading-tight sm:text-4xl'>
                  {t('marketing.community.title')}
                </h2>
                <p className='mt-4 max-w-2xl text-base leading-7 text-white/76'>
                  {t('marketing.community.description')}
                </p>

                <div className='mt-8 grid gap-4 sm:grid-cols-2'>
                  <CommunityPill
                    title={t('marketing.community.pillOneTitle')}
                    description={t('marketing.community.pillOneDescription')}
                  />
                  <CommunityPill
                    title={t('marketing.community.pillTwoTitle')}
                    description={t('marketing.community.pillTwoDescription')}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className='border-border/60 bg-card shadow-sm'>
              <CardContent className='flex h-full flex-col justify-between p-8'>
                <div>
                  <p className='text-sm font-semibold uppercase tracking-[0.24em] text-[hsl(var(--brand-electric))]'>
                    {t('marketing.detailCallout.eyebrow')}
                  </p>
                  <h3 className='mt-4 text-2xl font-semibold text-card-foreground'>
                    {t('marketing.detailCallout.title')}
                  </h3>
                  <p className='mt-3 text-sm leading-7 text-muted-foreground'>
                    {t('marketing.detailCallout.description')}
                  </p>
                </div>

                <div className='mt-8 flex flex-col gap-3'>
                  <Button asChild className='rounded-full bg-[hsl(var(--brand-navy))] text-white hover:bg-[hsl(var(--brand-navy-deep))]'>
                    <Link to='/features'>
                      {t('marketing.detailCallout.primary')}
                      <ArrowRight className='ml-2 h-4 w-4' />
                    </Link>
                  </Button>
                  <Button asChild variant='outline' className='rounded-full'>
                    <Link to='/login2'>{t('marketing.detailCallout.secondary')}</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
    </div>
  );
};

const CommunityPill = ({ title, description }) => (
  <div className='rounded-3xl border border-white/12 bg-white/10 p-5 backdrop-blur-sm'>
    <h3 className='text-base font-semibold'>{title}</h3>
    <p className='mt-2 text-sm leading-6 text-white/74'>{description}</p>
  </div>
);

const SectionHeading = ({ eyebrow, title, description }) => (
  <div className='max-w-3xl'>
    <p className='text-sm font-semibold uppercase tracking-[0.24em] text-[hsl(var(--brand-electric))]'>
      {eyebrow}
    </p>
    <h2 className='mt-4 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl'>
      {title}
    </h2>
    <p className='mt-4 text-base leading-7 text-muted-foreground'>{description}</p>
  </div>
);

export default HomePage;
